import { useEffect, useState } from 'react';
import { CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import {
  useOperatorPaymentGateway,
  useReplaceOperatorGatewayCredentials,
  useSetOperatorGatewayEnabled,
} from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { ConfirmModal, SectionHeader, Spinner } from '../../components/ui';
import { cn, fmtDateTime } from '../../lib/utils';

// Controlled, provider-agnostic messages. A raw Razorpay or backend error body
// is never rendered, so provider detail cannot leak into the Operator's screen.
const CREDENTIAL_ERROR_BY_STATUS = {
  400: 'Razorpay did not accept these credentials. Check the Key ID, Key Secret and mode, then try again. Your previous configuration has not been changed.',
  403: 'This account is not allowed to configure a payment gateway.',
  503: 'Credential validation is temporarily unavailable. Please try again later. Your previous configuration has not been changed.',
};
const CREDENTIAL_ERROR_FALLBACK =
  'The credentials could not be saved. Your previous configuration has not been changed.';

const ENABLE_ERROR_BY_STATUS = {
  400: 'The gateway could not be enabled. Save a validated Key ID and Key Secret first.',
  403: 'This account is not allowed to change the payment gateway.',
  503: 'This change is temporarily unavailable. Please try again later.',
};
const ENABLE_ERROR_FALLBACK = 'The gateway setting could not be changed. Please try again.';

const ROUTING_NOTE = {
  legacy_central: 'Customer payments are currently collected through the central AlgoSys payment account, not your Razorpay account.',
  paused: 'New customer subscription payments are currently paused by AlgoSys.',
};

const ACCOUNT_STATUS = {
  not_configured: {
    label: 'Not Configured',
    tone: 'muted',
    description: 'Add your Razorpay Key ID and Key Secret to connect your account.',
  },
  disabled: {
    label: 'Disabled',
    tone: 'muted',
    description: 'Your credentials are saved. Customer payments are not sent to this account while the gateway is disabled.',
  },
  legacy_central: {
    label: 'Central Payment Mode',
    tone: 'info',
    description: ROUTING_NOTE.legacy_central,
  },
  paused: {
    label: 'Payment Routing Paused',
    tone: 'warn',
    description: ROUTING_NOTE.paused,
  },
  ready: {
    label: 'Ready to Receive Payments',
    tone: 'bull',
    description: null,
  },
  unavailable: {
    label: 'Unavailable',
    tone: 'warn',
    description: 'Your gateway is configured and enabled, but it is not currently eligible to receive customer payments. Your saved credentials are unchanged. Contact AlgoSys support if this continues.',
  },
};

function controlledMessage(error, byStatus, fallback) {
  return byStatus[error?.status] ?? fallback;
}

// "Ready" requires the backend's own eligibility answer; any unknown or
// inconsistent routing status falls through to "Unavailable".
function resolveAccountStatus(gateway) {
  if (gateway?.configured !== true) return 'not_configured';
  if (gateway?.enabled !== true) return 'disabled';
  if (gateway?.routing_status === 'legacy_central') return 'legacy_central';
  if (gateway?.routing_status === 'paused') return 'paused';
  if (gateway?.routing_available === true && gateway?.routing_status === 'available') return 'ready';
  return 'unavailable';
}

function StatusPill({ tone, children }) {
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono uppercase tracking-wide',
      tone === 'bull' && 'text-bull bg-bull/10 border-bull/30',
      tone === 'warn' && 'text-warn bg-warn/10 border-warn/30',
      tone === 'info' && 'text-info bg-info/10 border-info/30',
      tone === 'muted' && 'text-txt-muted border-bg-border',
    )}>
      {children}
    </span>
  );
}

function Detail({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</dt>
      <dd className="text-xs text-txt-primary mt-0.5 break-words">{children}</dd>
    </div>
  );
}

export default function OperatorPaymentSettingsPage() {
  const role = useSessionStore((s) => s.role);
  // Exact Operator role only. The backend remains the authority on every
  // request; this is a UI guard so the interface is not shown to other roles.
  const isOperator = role === 'operator';

  const { data: gateway, isLoading, error } = useOperatorPaymentGateway({ enabled: isOperator });
  const {
    mutateAsync: replaceCredentials,
    reset: resetCredentialMutation,
    isPending: savingCredentials,
  } = useReplaceOperatorGatewayCredentials();
  const { mutateAsync: setGatewayEnabled, isPending: savingEnabled } = useSetOperatorGatewayEnabled();

  const [formOpen, setFormOpen] = useState(false);
  const [keyId, setKeyId] = useState('');
  // Transient component state only. Never written to localStorage,
  // sessionStorage, a URL, a log, or the React Query cache; cleared after every
  // submit attempt, when the form is closed, and when this page unmounts.
  const [keySecret, setKeySecret] = useState('');
  const [mode, setMode] = useState('test');
  const [formError, setFormError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [enableError, setEnableError] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);

  // Leaving the page must not leave the submitted secret behind in the
  // mutation's retained variables.
  useEffect(() => () => resetCredentialMutation(), [resetCredentialMutation]);

  if (!isOperator) {
    return (
      <div className="p-6 max-w-3xl">
        <SectionHeader title="Payment Gateway" />
        <p className="text-xs text-txt-muted mt-3">
          Payment gateway settings are available to Operator accounts only.
        </p>
      </div>
    );
  }

  const configured = gateway?.configured === true;
  const enabled = gateway?.enabled === true;
  const validated = gateway?.validation_status === 'validated';
  const statusKey = resolveAccountStatus(gateway);
  const status = ACCOUNT_STATUS[statusKey];
  // Deployment-level routing facts still apply while the Operator's own
  // gateway is unconfigured or disabled.
  const routingNote = (statusKey === 'not_configured' || statusKey === 'disabled')
    ? ROUTING_NOTE[gateway?.routing_status] ?? null
    : null;
  const showForm = !configured || formOpen;

  function clearCredentialInputs() {
    setKeyId('');
    setKeySecret('');
    setFormError(null);
    resetCredentialMutation();
  }

  function openForm() {
    clearCredentialInputs();
    setNotice(null);
    setMode(gateway?.mode ?? 'test');
    setFormOpen(true);
  }

  function closeForm() {
    clearCredentialInputs();
    setConfirmReplace(false);
    setFormOpen(false);
  }

  async function submitCredentials() {
    setFormError(null);
    setNotice(null);
    try {
      await replaceCredentials({ key_id: keyId.trim(), key_secret: keySecret, mode });
      setNotice('Credentials saved and validated with Razorpay. The Key Secret is not stored in this browser and is never shown again.');
      setKeyId('');
      setFormOpen(false);
    } catch (err) {
      setFormError(controlledMessage(err, CREDENTIAL_ERROR_BY_STATUS, CREDENTIAL_ERROR_FALLBACK));
    } finally {
      setKeySecret('');
      resetCredentialMutation();
    }
  }

  function handleSaveClick() {
    setFormError(null);
    setNotice(null);
    if (!keyId.trim() || !keySecret) {
      setFormError('Enter both the Razorpay Key ID and Key Secret. Changing credentials requires the complete new pair.');
      return;
    }
    if (configured) {
      setConfirmReplace(true);
      return;
    }
    submitCredentials();
  }

  async function handleToggleEnabled(nextEnabled) {
    setEnableError(null);
    setNotice(null);
    try {
      await setGatewayEnabled(nextEnabled);
    } catch (err) {
      setEnableError(controlledMessage(err, ENABLE_ERROR_BY_STATUS, ENABLE_ERROR_FALLBACK));
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-3xl">
      <SectionHeader
        title="Payment Gateway"
        subtitle="Connect your own Razorpay account for customer subscription payments."
      />

      <section className="border border-bg-border rounded-lg p-4 space-y-4" aria-label="Razorpay Account">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-txt-primary">
            <CreditCard size={15} />
            Razorpay Account
          </div>
          {!isLoading && !error && (
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          )}
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <p className="text-xs text-bear">
            Your payment gateway settings could not be loaded right now.
          </p>
        ) : (
          <>
            {(status.description || routingNote) && (
              <div className="space-y-1">
                {status.description && <p className="text-xs text-txt-secondary">{status.description}</p>}
                {routingNote && <p className="text-xs text-txt-secondary">{routingNote}</p>}
              </div>
            )}

            {configured && (
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
                <Detail label="Key ID">
                  <span className="font-mono">{gateway?.masked_key_id ?? '—'}</span>
                </Detail>
                <Detail label="Mode">
                  {gateway?.mode === 'live' ? 'Live' : gateway?.mode === 'test' ? 'Test' : '—'}
                </Detail>
                <Detail label="Credentials">
                  {validated ? 'Validated' : 'Not validated'}
                </Detail>
                {gateway?.validated_at && (
                  <Detail label="Last validated">{fmtDateTime(gateway.validated_at)}</Detail>
                )}
              </dl>
            )}

            {configured && (
              <div className="flex flex-wrap items-center gap-2">
                {enabled ? (
                  <button
                    onClick={() => handleToggleEnabled(false)}
                    disabled={savingEnabled}
                    title="Disabling keeps your saved credentials and payment history"
                    className="btn-ghost text-xs border border-bg-border flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {savingEnabled && <Loader2 size={12} className="animate-spin" />}
                    Disable Gateway
                  </button>
                ) : (
                  <button
                    onClick={() => handleToggleEnabled(true)}
                    disabled={savingEnabled || !validated}
                    title={validated ? 'Enable your Razorpay account' : 'Save validated credentials first'}
                    className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {savingEnabled && <Loader2 size={12} className="animate-spin" />}
                    Enable Gateway
                  </button>
                )}
                {!formOpen && (
                  <button
                    onClick={openForm}
                    className="btn-ghost text-xs border border-bg-border"
                  >
                    Change Credentials
                  </button>
                )}
              </div>
            )}

            {enableError && (
              <p className="text-xs text-bear bg-bear/5 border border-bear/30 rounded px-3 py-2">{enableError}</p>
            )}
            {notice && (
              <p className="text-xs text-bull bg-bull/5 border border-bull/30 rounded px-3 py-2">{notice}</p>
            )}

            {showForm && (
              <div className="border-t border-bg-border pt-4 space-y-3">
                <div className="text-xs font-medium text-txt-primary">
                  {configured ? 'Change Credentials' : 'Connect Razorpay'}
                </div>
                <p className="text-2xs text-txt-muted">
                  Enter the Key ID and Key Secret from your Razorpay dashboard. AlgoSys validates them with
                  Razorpay before saving. Your saved Key Secret is never shown, so a change always needs the
                  complete new pair.
                </p>

                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="space-y-1 block">
                    <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Razorpay Key ID</span>
                    <input
                      type="text"
                      value={keyId}
                      onChange={(e) => setKeyId(e.target.value)}
                      placeholder="rzp_test_…"
                      autoComplete="off"
                      spellCheck={false}
                      className="input-base w-full text-xs"
                    />
                  </label>
                  <label className="space-y-1 block">
                    <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Razorpay Key Secret</span>
                    <input
                      type="password"
                      value={keySecret}
                      onChange={(e) => setKeySecret(e.target.value)}
                      placeholder="Enter the new Key Secret"
                      autoComplete="new-password"
                      spellCheck={false}
                      className="input-base w-full text-xs"
                    />
                  </label>
                  <label className="space-y-1 block">
                    <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Mode</span>
                    <select
                      value={mode}
                      onChange={(e) => setMode(e.target.value)}
                      className="input-field w-full text-xs"
                    >
                      <option value="test">Test</option>
                      <option value="live">Live</option>
                    </select>
                  </label>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={handleSaveClick}
                    disabled={savingCredentials}
                    className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {savingCredentials ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                    {savingCredentials ? 'Validating…' : 'Save & Validate'}
                  </button>
                  {configured && (
                    <button
                      onClick={closeForm}
                      disabled={savingCredentials}
                      className="btn-ghost text-xs disabled:opacity-50"
                    >
                      Close
                    </button>
                  )}
                </div>

                {formError && (
                  <p className="text-xs text-bear bg-bear/5 border border-bear/30 rounded px-3 py-2">{formError}</p>
                )}
              </div>
            )}
          </>
        )}

        <p className="text-2xs text-txt-muted border-t border-bg-border/60 pt-3">
          License recharges are always paid to AlgoSys.
        </p>
      </section>

      <ConfirmModal
        open={confirmReplace}
        title="Replace Razorpay credentials?"
        message={
          'The new Key ID and Key Secret will replace your current ones after AlgoSys validates them with Razorpay. '
          + 'Payments already received stay linked to the credential version that processed them, and your payment history is not changed. '
          + 'The gateway stays in its current enabled or disabled state.'
        }
        confirmLabel="Replace & Validate"
        submitting={savingCredentials}
        submittingLabel="Validating…"
        onCancel={() => setConfirmReplace(false)}
        onConfirm={() => { setConfirmReplace(false); submitCredentials(); }}
      />
    </div>
  );
}
